// src/pages/AdminPanel.tsx
// Status Board specific admin panel for managing rooms, investigators, and settings
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';
import {
  Upload, Settings, Home, LogOut, Plus, Edit2,
  Save, X, AlertCircle, CheckCircle, Eye, EyeOff,
  Stethoscope, LayoutGrid, Trash2, Link2, Users, Ban
} from 'lucide-react';
import SiteSelector from '../components/SiteSelector';
import { ThemeToggle } from '../theme/ThemeToggle';
import './AdminPanel.css';

interface Room {
  id: string;
  room_name: string;
  is_active: boolean;
}

interface Investigator {
  id: string;
  site_id: string;
  name: string;
  display_order: number;
  is_active: boolean;
}

interface ColorThresholds {
  green: number;
  yellow: number;
  red: number;
}

// eReg P4 (§5.4): study-string → registry mapping
interface RegistryProtocolRef {
  id: string;
  protocol_number: string;
  study_nickname?: string | null;
  sponsor_name?: string | null;
  archived_at?: string | null;
}

interface StudyMapping {
  id: string;
  study_name: string;
  protocol_id: string;
  protocol?: RegistryProtocolRef | null;
}

interface UnmappedStudyName {
  study_name: string;
  appointment_count: number;
  last_seen: string | null;
  suggestions: { protocol: RegistryProtocolRef; score: number; exact: boolean }[];
}

// Ops Insights: CRC first-name → user mapping
interface SiteUserRef {
  id: string;
  full_name: string;
  email?: string | null;
}

// A row with `user_id: null` (and therefore no `user` embed) is a BLOCK, not a
// mapping: unmapping a name leaves this tombstone behind so the auto-mapper
// stops re-claiming it on the next sync. Deleting a blocked row is the unblock.
interface CrcMapping {
  id: string;
  crc_name: string;
  user_id: string | null;
  user?: SiteUserRef | null;
}

interface UnmappedCrcName {
  crc_name: string;
  appointment_count: number;
  last_seen: string | null;
  suggestions: { user: SiteUserRef; exact: boolean }[];
}

/** A tombstone row (no user) = the name is blocked from auto-mapping. */
const isBlockedCrc = (mapping: CrcMapping) => !mapping.user_id;

const AdminPanel: React.FC = () => {
  const { currentSite, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Entitlement check state
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);
  const [entitlementChecked, setEntitlementChecked] = useState(false);

  const [activeTab, setActiveTab] = useState('upload');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [colorThresholds, setColorThresholds] = useState<ColorThresholds>({
    green: 15,
    yellow: 30,
    red: 45
  });
  const [newRoom, setNewRoom] = useState('');
  const [editingRoom, setEditingRoom] = useState<string | null>(null);
  const [editingRoomName, setEditingRoomName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  // Investigator management
  const [investigators, setInvestigators] = useState<Investigator[]>([]);
  const [newInvestigator, setNewInvestigator] = useState('');
  const [editingInvestigator, setEditingInvestigator] = useState<string | null>(null);
  const [editingInvestigatorName, setEditingInvestigatorName] = useState('');

  // eReg P4 (§5.4): study mapping tab
  const [studyMappings, setStudyMappings] = useState<StudyMapping[]>([]);
  const [unmappedStudies, setUnmappedStudies] = useState<UnmappedStudyName[]>([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingMessage, setMappingMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  // Rapid site switches must not let an older response land last
  const mappingFetchSeq = React.useRef(0);

  // Ops Insights: CRC mapping tab
  const [crcMappings, setCrcMappings] = useState<CrcMapping[]>([]);
  const [unmappedCrcs, setUnmappedCrcs] = useState<UnmappedCrcName[]>([]);
  const [crcEmptyCount, setCrcEmptyCount] = useState(0);
  const [crcLoading, setCrcLoading] = useState(false);
  const [crcLoadFailed, setCrcLoadFailed] = useState(false);
  const [crcMessage, setCrcMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const crcFetchSeq = React.useRef(0);
  // GET /api/crc-name-map returns both real mappings and block tombstones
  // (user_id null); they are different states and render as different sections.
  const mappedCrcs = crcMappings.filter((m) => !isBlockedCrc(m));
  const blockedCrcs = crcMappings.filter(isBlockedCrc);

  useEffect(() => {
    if (currentSite) {
      fetchRooms();
      fetchSettings();
      fetchInvestigators();
    }
  }, [currentSite?.id]);

  // eReg P4 (§5.4): load mapping data when the tab is opened (or site changes)
  useEffect(() => {
    if (activeTab === 'study-mapping' && currentSite) {
      fetchStudyMapping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentSite?.id]);

  // Ops Insights: load CRC mapping data when the tab is opened (or site changes)
  useEffect(() => {
    if (activeTab === 'crc-mapping' && currentSite) {
      fetchCrcMapping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentSite?.id]);

  // Check Status Board entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (user?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const statusBoardTool = tools.find((t: any) => t.tool_id === 'status-board');
        setHasEntitlement(statusBoardTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking Status Board entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    checkEntitlement();
  }, [user?.role, currentSite]);

  const fetchRooms = async () => {
    try {
      const response = await api.get('/api/rooms');
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      if (response.data.color_thresholds) {
        setColorThresholds(response.data.color_thresholds);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchInvestigators = async () => {
    if (!currentSite) return;
    try {
      const response = await api.get(`/api/sites/${currentSite.id}/investigators`);
      setInvestigators(response.data);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  // eReg P4 (§5.4): unmapped study names + existing mappings
  const fetchStudyMapping = async () => {
    if (!currentSite) return;
    const seq = ++mappingFetchSeq.current;
    setMappingLoading(true);
    try {
      const [unmappedRes, mappedRes] = await Promise.all([
        api.get(`/api/study-name-map/unmapped?site_id=${currentSite.id}`),
        api.get(`/api/study-name-map?site_id=${currentSite.id}`),
      ]);
      if (seq !== mappingFetchSeq.current) return; // a newer fetch superseded this one
      setUnmappedStudies(Array.isArray(unmappedRes.data) ? unmappedRes.data : []);
      setStudyMappings(Array.isArray(mappedRes.data) ? mappedRes.data : []);
    } catch (error) {
      if (seq !== mappingFetchSeq.current) return;
      console.error('Error fetching study mapping:', error);
      setMappingMessage({ type: 'error', message: 'Failed to load study mapping data' });
    } finally {
      if (seq === mappingFetchSeq.current) setMappingLoading(false);
    }
  };

  const mapStudyName = async (studyName: string, protocolId: string) => {
    if (!currentSite) return;
    setMappingMessage(null);
    try {
      await api.post('/api/study-name-map', {
        site_id: currentSite.id,
        study_name: studyName,
        protocol_id: protocolId,
      });
      setMappingMessage({ type: 'success', message: `Mapped "${studyName}"` });
      fetchStudyMapping();
    } catch (error: any) {
      setMappingMessage({ type: 'error', message: error.response?.data?.error || 'Failed to map study name' });
    }
  };

  const unmapStudyName = async (mapping: StudyMapping) => {
    setMappingMessage(null);
    try {
      await api.delete(`/api/study-name-map/${mapping.id}`);
      setMappingMessage({ type: 'success', message: `Unmapped "${mapping.study_name}"` });
      fetchStudyMapping();
    } catch (error: any) {
      setMappingMessage({ type: 'error', message: error.response?.data?.error || 'Failed to unmap study name' });
    }
  };

  // Ops Insights: unmapped CRC names + existing mappings
  const fetchCrcMapping = async () => {
    if (!currentSite) return;
    const seq = ++crcFetchSeq.current;
    setCrcLoading(true);
    // Clear stale data up front so a failed fetch (e.g. after switching
    // sites) can't leave the previous site's mappings on screen looking
    // like a valid, current result.
    setUnmappedCrcs([]);
    setCrcEmptyCount(0);
    setCrcMappings([]);
    try {
      const [unmappedRes, mappedRes] = await Promise.all([
        api.get(`/api/crc-name-map/unmapped?site_id=${currentSite.id}`),
        api.get(`/api/crc-name-map?site_id=${currentSite.id}`),
      ]);
      if (seq !== crcFetchSeq.current) return; // a newer fetch superseded this one
      setUnmappedCrcs(Array.isArray(unmappedRes.data?.names) ? unmappedRes.data.names : []);
      setCrcEmptyCount(unmappedRes.data?.empty_count || 0);
      setCrcMappings(Array.isArray(mappedRes.data) ? mappedRes.data : []);
      setCrcLoadFailed(false);
    } catch (error) {
      if (seq !== crcFetchSeq.current) return;
      console.error('Error fetching CRC mapping:', error);
      setCrcMessage({ type: 'error', message: 'Failed to load CRC mapping data' });
      setCrcLoadFailed(true);
    } finally {
      if (seq === crcFetchSeq.current) setCrcLoading(false);
    }
  };

  const mapCrcName = async (crcName: string, userId: string) => {
    if (!currentSite) return;
    setCrcMessage(null);
    try {
      await api.post('/api/crc-name-map', {
        site_id: currentSite.id,
        crc_name: crcName,
        user_id: userId,
      });
      setCrcMessage({ type: 'success', message: `Mapped "${crcName}"` });
      fetchCrcMapping();
    } catch (error: any) {
      setCrcMessage({ type: 'error', message: error.response?.data?.error || 'Failed to map CRC name' });
    }
  };

  // DELETE is durable-unmap: on a MAPPED row it blocks the name (the row
  // survives with user_id null, so the hourly auto-mapper stops re-claiming
  // it); on an already-BLOCKED row it removes the row entirely, which is the
  // unblock. Same endpoint, and the row's own state decides which one happens.
  const unmapCrcName = async (mapping: CrcMapping) => {
    const wasBlocked = isBlockedCrc(mapping);
    setCrcMessage(null);
    try {
      // The response says which branch ran (`{ blocked: true }` vs
      // `{ deleted: true }`), so the message never has to guess from a row
      // that may have changed under us.
      const response = await api.delete(`/api/crc-name-map/${mapping.id}`);
      const blockedNow = response.data?.blocked === true;
      setCrcMessage({
        type: 'success',
        message: blockedNow
          ? `Unmapped "${mapping.crc_name}" — it is now blocked from auto-mapping`
          : `Unblocked "${mapping.crc_name}" — it can be auto-mapped again`,
      });
      fetchCrcMapping();
    } catch (error: any) {
      setCrcMessage({
        type: 'error',
        message:
          error.response?.data?.error || (wasBlocked ? 'Failed to remove the block' : 'Failed to unmap CRC name'),
      });
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    setLoading(true);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await api.post('/api/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus({
        type: 'success',
        message: response.data.message || 'File uploaded successfully'
      });
      setUploadFile(null);
    } catch (error: any) {
      setUploadStatus({
        type: 'error',
        message: error.response?.data?.error || 'Error uploading file'
      });
    } finally {
      setLoading(false);
    }
  };

  const addRoom = async () => {
    if (!newRoom.trim()) return;

    try {
      await api.post('/api/rooms', { room_name: newRoom });
      setNewRoom('');
      fetchRooms();
    } catch (error) {
      console.error('Error adding room:', error);
      alert('Error adding room');
    }
  };

  const updateRoom = async (roomId: string) => {
    if (!editingRoomName.trim()) return;

    try {
      await api.patch(`/api/rooms/${roomId}`, { room_name: editingRoomName });
      setEditingRoom(null);
      fetchRooms();
    } catch (error) {
      console.error('Error updating room:', error);
      alert('Error updating room');
    }
  };

  const toggleRoomActive = async (roomId: string, currentStatus: boolean) => {
    try {
      await api.patch(`/api/rooms/${roomId}`, { is_active: !currentStatus });
      fetchRooms();
    } catch (error) {
      console.error('Error toggling room status:', error);
      alert('Error updating room status');
    }
  };

  const updateColorThresholds = async () => {
    try {
      await api.patch('/api/settings', {
        setting_key: 'color_thresholds',
        setting_value: colorThresholds
      });
      alert('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Error updating settings');
    }
  };

  // Investigator management functions
  const addInvestigator = async () => {
    if (!newInvestigator.trim() || !currentSite) return;

    try {
      await api.post(`/api/sites/${currentSite.id}/investigators`, {
        name: newInvestigator,
        display_order: investigators.length
      });
      setNewInvestigator('');
      fetchInvestigators();
    } catch (error) {
      console.error('Error adding investigator:', error);
      alert('Error adding investigator');
    }
  };

  const updateInvestigator = async (invId: string) => {
    if (!editingInvestigatorName.trim() || !currentSite) return;

    try {
      await api.patch(`/api/sites/${currentSite.id}/investigators/${invId}`, {
        name: editingInvestigatorName
      });
      setEditingInvestigator(null);
      fetchInvestigators();
    } catch (error) {
      console.error('Error updating investigator:', error);
      alert('Error updating investigator');
    }
  };

  const deleteInvestigator = async (invId: string) => {
    if (!currentSite || !window.confirm('Are you sure you want to delete this investigator?')) return;

    try {
      await api.delete(`/api/sites/${currentSite.id}/investigators/${invId}`);
      fetchInvestigators();
    } catch (error) {
      console.error('Error deleting investigator:', error);
      alert('Error deleting investigator');
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Show loading while checking entitlement
  if (checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show Access Denied if user doesn't have Status Board entitlement
  if (entitlementChecked && !hasEntitlement && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to the Status Board for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
        <h2>Status Board Admin</h2>
        <div className="sidebar-site-selector">
          <SiteSelector />
        </div>
        <nav className="admin-nav">
          <button
            className={activeTab === 'upload' ? 'active' : ''}
            onClick={() => setActiveTab('upload')}
          >
            <Upload size={16} />
            Upload CSV
          </button>
          <button
            className={activeTab === 'rooms' ? 'active' : ''}
            onClick={() => setActiveTab('rooms')}
          >
            <Home size={16} />
            Manage Rooms
          </button>
          <button
            className={activeTab === 'investigators' ? 'active' : ''}
            onClick={() => setActiveTab('investigators')}
          >
            <Stethoscope size={16} />
            Investigators
          </button>
          <button
            className={activeTab === 'study-mapping' ? 'active' : ''}
            onClick={() => setActiveTab('study-mapping')}
          >
            <Link2 size={16} />
            Study Mapping
          </button>
          <button
            className={activeTab === 'crc-mapping' ? 'active' : ''}
            onClick={() => setActiveTab('crc-mapping')}
          >
            <Users size={16} />
            CRC Mapping
          </button>
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={16} />
            Settings
          </button>
        </nav>
        <div className="sidebar-footer">
          <button onClick={() => navigate('/')} className="nav-button">
            <LayoutGrid size={16} />
            Tools
          </button>
          <button onClick={() => navigate('/status-board')} className="nav-button">
            <Home size={16} />
            Status Board
          </button>
          <button onClick={handleSignOut} className="nav-button">
            <LogOut size={16} />
            Sign Out
          </button>
          <ThemeToggle variant="neutral" className="sidebar-theme-toggle" />
        </div>
      </div>

      <div className="admin-content">
        {/* Upload CSV Tab */}
        {activeTab === 'upload' && (
          <div className="tab-content">
            <h3>Upload Daily CSV {currentSite && `- ${currentSite.name}`}</h3>
            <form onSubmit={handleFileUpload} className="upload-form">
              <div className="file-input-wrapper">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  id="csv-file"
                />
                <label htmlFor="csv-file" className="file-label">
                  {uploadFile ? uploadFile.name : 'Choose CSV file...'}
                </label>
              </div>

              {uploadStatus && (
                <div className={`status-message ${uploadStatus.type}`}>
                  {uploadStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  {uploadStatus.message}
                </div>
              )}

              <button type="submit" className="upload-button" disabled={!uploadFile || loading}>
                {loading ? 'Uploading...' : 'Upload File'}
              </button>
            </form>

            <div className="info-section">
              <h4>CSV Format Requirements:</h4>
              <ul>
                <li>Subject ID: Unique patient identifier</li>
                <li>Initials: Patient initials</li>
                <li>Protocol Number: Study/protocol name</li>
                <li>Scheduled Time: Appointment date/time</li>
                <li>Name: Visit type/name</li>
                <li>Full Name: CRC full name (first name will be used)</li>
              </ul>
              <p className="warning">
                <AlertCircle size={16} />
                Uploading a new CSV will replace all appointments for today at this site
              </p>
            </div>
          </div>
        )}

        {/* eReg P4 (§5.4): Study Mapping Tab — resolve free-text study names
            to registry studies. Mapping is additive metadata over the Looker
            sync (§6-E8); it never blocks a sync and never creates studies. */}
        {activeTab === 'study-mapping' && (
          <div className="tab-content">
            <h3>Study Mapping {currentSite && `- ${currentSite.name}`}</h3>
            <p className="mapping-intro">
              Map the study names arriving on appointments to studies in the platform registry.
              Exact protocol-number matches are mapped automatically by the hourly sync; anything
              else lands here for a one-click decision. Mapped studies show their nickname and a
              documents link on the board.
            </p>

            {mappingMessage && (
              <div className={`status-message ${mappingMessage.type}`}>
                {mappingMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {mappingMessage.message}
              </div>
            )}

            {mappingLoading ? (
              <p className="mapping-empty">Loading…</p>
            ) : (
              <>
                <h4 className="mapping-section-title">
                  Unmapped study names {unmappedStudies.length > 0 && `(${unmappedStudies.length})`}
                </h4>
                {unmappedStudies.length === 0 ? (
                  <p className="mapping-empty">
                    Nothing to map — every study name seen in the last 90 days is mapped.
                  </p>
                ) : (
                  <div className="rooms-list mapping-list">
                    {unmappedStudies.map((item) => (
                      <div key={item.study_name} className="room-item mapping-item">
                        <div className="mapping-item-main">
                          <span className="room-name">{item.study_name}</span>
                          <span className="mapping-item-meta">
                            {item.appointment_count} appointment{item.appointment_count === 1 ? '' : 's'}
                            {item.last_seen ? ` · last seen ${item.last_seen}` : ''}
                          </span>
                        </div>
                        <div className="mapping-item-actions">
                          {item.suggestions.length === 0 ? (
                            <span className="mapping-item-meta">No registry match — create the study in eReg first</span>
                          ) : (
                            item.suggestions.map((s) => (
                              <button
                                key={s.protocol.id}
                                className="mapping-suggest-button"
                                onClick={() => mapStudyName(item.study_name, s.protocol.id)}
                                title={s.protocol.study_nickname || s.protocol.protocol_number}
                              >
                                <Link2 size={14} />
                                Map to {s.protocol.protocol_number}
                                {s.exact ? '' : ` (${s.score}% match)`}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <h4 className="mapping-section-title">
                  Mapped study names {studyMappings.length > 0 && `(${studyMappings.length})`}
                </h4>
                {studyMappings.length === 0 ? (
                  <p className="mapping-empty">No study names are mapped yet.</p>
                ) : (
                  <div className="rooms-list mapping-list">
                    {studyMappings.map((mapping) => (
                      <div key={mapping.id} className="room-item mapping-item">
                        <div className="mapping-item-main">
                          <span className="room-name">
                            {mapping.study_name}
                            <span className="mapping-arrow"> → </span>
                            {mapping.protocol?.protocol_number || 'Unknown study'}
                            {mapping.protocol?.study_nickname ? ` (${mapping.protocol.study_nickname})` : ''}
                          </span>
                          {mapping.protocol?.archived_at && (
                            <span className="mapping-item-meta">Archived in eReg</span>
                          )}
                        </div>
                        <div className="mapping-item-actions">
                          <button
                            className="icon-button"
                            onClick={() => unmapStudyName(mapping)}
                            title="Remove mapping"
                            aria-label={`Unmap ${mapping.study_name}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Ops Insights: CRC Mapping Tab — resolve free-text CRC first names
            to platform users. Mapping is additive metadata over the untouched
            ingest paths; the sync auto-maps unambiguous first-name matches and
            everything else lands here for a one-click decision. */}
        {activeTab === 'crc-mapping' && (
          <div className="tab-content">
            <h3>CRC Mapping {currentSite && `- ${currentSite.name}`}</h3>
            <p className="mapping-intro">
              Map the coordinator first names arriving on appointments to user accounts.
              Unambiguous matches are mapped automatically by the hourly sync; shared or
              misspelled first names land here for a one-click decision. Mappings power the
              Operations Insights tool and apply to past appointments too. Removing a mapping also
              blocks that name from being auto-mapped again, so a correction sticks; removing the
              block returns the name to the unmapped list.
            </p>

            {crcMessage && (
              <div className={`status-message ${crcMessage.type}`}>
                {crcMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {crcMessage.message}
              </div>
            )}

            {crcLoading ? (
              <p className="mapping-empty">Loading…</p>
            ) : (
              <>
                {crcEmptyCount > 0 && (
                  <p className="mapping-empty">
                    {crcEmptyCount} appointment{crcEmptyCount === 1 ? '' : 's'} in the last 90 days
                    {crcEmptyCount === 1 ? ' has' : ' have'} no CRC name at all — those can't be
                    mapped and count as “Unattributed” in Operations Insights.
                  </p>
                )}

                <h4 className="mapping-section-title">
                  Unmapped CRC names {unmappedCrcs.length > 0 && `(${unmappedCrcs.length})`}
                </h4>
                {crcLoadFailed ? null : unmappedCrcs.length === 0 ? (
                  <p className="mapping-empty">
                    Nothing to map — every CRC name seen in the last 90 days is either mapped or
                    blocked from auto-mapping.
                  </p>
                ) : (
                  <div className="rooms-list mapping-list">
                    {unmappedCrcs.map((item) => (
                      <div key={item.crc_name} className="room-item mapping-item">
                        <div className="mapping-item-main">
                          <span className="room-name">{item.crc_name}</span>
                          <span className="mapping-item-meta">
                            {item.appointment_count} appointment{item.appointment_count === 1 ? '' : 's'}
                            {item.last_seen ? ` · last seen ${item.last_seen}` : ''}
                          </span>
                        </div>
                        <div className="mapping-item-actions">
                          {item.suggestions.length === 0 ? (
                            <span className="mapping-item-meta">No matching user at this site</span>
                          ) : (
                            item.suggestions.map((s) => (
                              <button
                                key={s.user.id}
                                className="mapping-suggest-button"
                                onClick={() => mapCrcName(item.crc_name, s.user.id)}
                                title={s.user.email || s.user.full_name}
                              >
                                <Users size={14} />
                                Map to {s.user.full_name}
                                {s.exact ? '' : ' (partial match)'}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <h4 className="mapping-section-title">
                  Mapped CRC names {mappedCrcs.length > 0 && `(${mappedCrcs.length})`}
                </h4>
                {crcLoadFailed ? null : mappedCrcs.length === 0 ? (
                  <p className="mapping-empty">No CRC names are mapped yet.</p>
                ) : (
                  <div className="rooms-list mapping-list">
                    {mappedCrcs.map((mapping) => (
                      <div key={mapping.id} className="room-item mapping-item">
                        <div className="mapping-item-main">
                          <span className="room-name">
                            {mapping.crc_name}
                            <span className="mapping-arrow"> → </span>
                            {mapping.user?.full_name || 'Unknown user'}
                          </span>
                          {mapping.user?.email && (
                            <span className="mapping-item-meta">{mapping.user.email}</span>
                          )}
                        </div>
                        <div className="mapping-item-actions">
                          <button
                            className="icon-button"
                            onClick={() => unmapCrcName(mapping)}
                            title="Remove mapping and block this name from auto-mapping"
                            aria-label={`Unmap ${mapping.crc_name}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Blocked names: rows that survive with no user so the hourly
                    auto-mapper stops re-claiming a name an admin rejected.
                    Only shown when there are any — an empty section would be
                    noise on the common path. */}
                {!crcLoadFailed && blockedCrcs.length > 0 && (
                  <>
                    <h4 className="mapping-section-title">Blocked from auto-mapping ({blockedCrcs.length})</h4>
                    <p className="mapping-empty">
                      These names stay unattributed in Operations Insights and the hourly sync will
                      not claim them. Remove a block to return the name to the unmapped list above,
                      where it can be mapped — and where the sync can auto-map it again if it is an
                      unambiguous match.
                    </p>
                    <div className="rooms-list mapping-list">
                      {blockedCrcs.map((mapping) => (
                        <div key={mapping.id} className="room-item mapping-item blocked">
                          <div className="mapping-item-main">
                            <span className="room-name">{mapping.crc_name}</span>
                            <span className="mapping-blocked-badge">
                              <Ban size={12} aria-hidden="true" />
                              Blocked from auto-mapping
                            </span>
                          </div>
                          <div className="mapping-item-actions">
                            <button
                              className="icon-button"
                              onClick={() => unmapCrcName(mapping)}
                              title="Remove block"
                              aria-label={`Remove the auto-mapping block on ${mapping.crc_name}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Rooms Tab */}
        {activeTab === 'rooms' && (
          <div className="tab-content">
            <h3>Manage Rooms {currentSite && `- ${currentSite.name}`}</h3>
            <div className="add-room">
              <input
                type="text"
                value={newRoom}
                onChange={(e) => setNewRoom(e.target.value)}
                placeholder="New room name..."
                onKeyPress={(e) => e.key === 'Enter' && addRoom()}
              />
              <button onClick={addRoom} className="add-button">
                <Plus size={16} />
                Add Room
              </button>
            </div>

            <div className="rooms-list">
              {rooms.map(room => (
                <div key={room.id} className={`room-item ${!room.is_active ? 'inactive' : ''}`}>
                  {editingRoom === room.id ? (
                    <>
                      <input
                        type="text"
                        value={editingRoomName}
                        onChange={(e) => setEditingRoomName(e.target.value)}
                        className="edit-input"
                      />
                      <button onClick={() => updateRoom(room.id)} className="icon-button">
                        <Save size={16} />
                      </button>
                      <button onClick={() => setEditingRoom(null)} className="icon-button">
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="room-name">{room.room_name}</span>
                      <div className="room-actions">
                        <button
                          onClick={() => {
                            setEditingRoom(room.id);
                            setEditingRoomName(room.room_name);
                          }}
                          className="icon-button"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Investigators Tab */}
        {activeTab === 'investigators' && (
          <div className="tab-content">
            <h3>Manage Investigators {currentSite && `- ${currentSite.name}`}</h3>
            <div className="add-room">
              <input
                type="text"
                value={newInvestigator}
                onChange={(e) => setNewInvestigator(e.target.value)}
                placeholder="New investigator name..."
                onKeyPress={(e) => e.key === 'Enter' && addInvestigator()}
              />
              <button onClick={addInvestigator} className="add-button">
                <Plus size={16} />
                Add Investigator
              </button>
            </div>

            <div className="rooms-list">
              {investigators.map(inv => (
                <div key={inv.id} className="room-item">
                  {editingInvestigator === inv.id ? (
                    <>
                      <input
                        type="text"
                        value={editingInvestigatorName}
                        onChange={(e) => setEditingInvestigatorName(e.target.value)}
                        className="edit-input"
                      />
                      <button onClick={() => updateInvestigator(inv.id)} className="icon-button">
                        <Save size={16} />
                      </button>
                      <button onClick={() => setEditingInvestigator(null)} className="icon-button">
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="room-name">{inv.name}</span>
                      <div className="room-actions">
                        <button
                          onClick={() => {
                            setEditingInvestigator(inv.id);
                            setEditingInvestigatorName(inv.name);
                          }}
                          className="icon-button"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => deleteInvestigator(inv.id)}
                          className="icon-button danger"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {investigators.length === 0 && (
                <p className="empty-message">No investigators configured for this site.</p>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="tab-content">
            <h3>Site Settings {currentSite && `- ${currentSite.name}`}</h3>
            <div className="settings-section">
              <h4>Color Thresholds (minutes)</h4>
              <p className="settings-description">
                Set the time thresholds for color-coding patient wait times
              </p>

              <div className="threshold-inputs">
                <div className="threshold-group">
                  <label>
                    <span className="color-indicator green"></span>
                    Green (Normal)
                  </label>
                  <input
                    type="number"
                    value={colorThresholds.green}
                    onChange={(e) => setColorThresholds({ ...colorThresholds, green: parseInt(e.target.value) })}
                    min="1"
                    max="60"
                  />
                  <span className="unit">minutes</span>
                </div>

                <div className="threshold-group">
                  <label>
                    <span className="color-indicator yellow"></span>
                    Yellow (Warning)
                  </label>
                  <input
                    type="number"
                    value={colorThresholds.yellow}
                    onChange={(e) => setColorThresholds({ ...colorThresholds, yellow: parseInt(e.target.value) })}
                    min="1"
                    max="120"
                  />
                  <span className="unit">minutes</span>
                </div>

                <div className="threshold-group">
                  <label>
                    <span className="color-indicator red"></span>
                    Red (Critical)
                  </label>
                  <input
                    type="number"
                    value={colorThresholds.red}
                    onChange={(e) => setColorThresholds({ ...colorThresholds, red: parseInt(e.target.value) })}
                    min="1"
                    max="180"
                  />
                  <span className="unit">minutes</span>
                </div>
              </div>

              <button onClick={updateColorThresholds} className="save-button">
                <Save size={16} />
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
